import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import type { User } from '@/types/api';

interface UserDeleteDialogProps {
  isOpen: boolean;
  onClose: () => void;
  selectedUser: User | null;
  onConfirm: () => void;
}

export default function UserDeleteDialog({
  isOpen,
  onClose,
  selectedUser,
  onConfirm,
}: UserDeleteDialogProps) {
  const { t, i18n } = useTranslation();
  const isRTL = useMemo(() => i18n.language === 'ar', [i18n.language]);

  return (
    <AlertDialog open={isOpen} onOpenChange={onClose}>
      <AlertDialogContent className="max-w-[90vw] sm:max-w-[425px]">
        <AlertDialogHeader>
          <AlertDialogTitle
            className={`text-lg ${isRTL ? 'text-right' : 'text-left'}`}
          >
            {t('users.management.deleteDialog.title', 'Are you sure?')}
          </AlertDialogTitle>
          <AlertDialogDescription
            className={`text-sm ${isRTL ? 'text-right' : 'text-left'}`}
          >
            {t(
              'users.management.deleteDialog.description',
              'This action cannot be undone. This will permanently delete the user'
            )}{' '}
            <span className="font-semibold">{selectedUser?.name}</span>{' '}
            {t(
              'users.management.deleteDialog.andRemoveData',
              'and remove all associated data'
            )}
            .
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex-col sm:flex-row gap-2">
          <AlertDialogCancel className="w-full sm:w-auto">
            {t('users.management.deleteDialog.cancel', 'Cancel')}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            className="w-full sm:w-auto bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {t('users.management.deleteDialog.deleteUser', 'Delete User')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
